/**
 * Isolated frame-script runner.
 *
 * The Luau engine currently runs on the main thread. Host `print` messages are
 * collected during the sync Wasm/native call, then delivered to sinks via
 * `queueMicrotask` so UI/React updates are not nested inside the engine.
 *
 * Document APIs mutate an injected pages copy. `apply_changes` commits via the
 * host apply handler (mergeable single undo per runId).
 *
 * Realtime mode (`update` / `draw`) keeps a Wasm session and ticks at 30fps
 * via {@link ScriptRealtimeController}.
 */

import {
  ensureCurvaScriptWasm,
  type CurvaScriptWasm,
  type ScriptDocumentContext,
  type ScriptLogEntry,
  type ScriptRunResult,
  type ScriptTickResult,
} from "./curvaScriptWasm";
import { preloadScriptFonts } from "./scriptFonts";

export type { ScriptDocumentContext, ScriptLogEntry, ScriptRunResult, ScriptTickResult };

export const SCRIPT_REALTIME_FPS = 30;
export const SCRIPT_REALTIME_DT_SECONDS = 1 / SCRIPT_REALTIME_FPS;

/** Injectible sync engine backend (Wasm today; fakes in tests). */
export interface ScriptEngine {
  runFrameScript(source: string, context?: ScriptDocumentContext): ScriptRunResult;
  startSession?(source: string, context?: ScriptDocumentContext): ScriptRunResult;
  tickSession?(dtSeconds: number): ScriptTickResult;
  setInputState?(json: string): void;
  stopSession?(): void;
  setApplyHandler?: (handler: (runId: string, pagesJson: string) => void) => void;
  clearApplyHandler?: () => void;
  setImageHandler?: (
    handler: (
      runId: string,
      width: number,
      height: number,
      rgba: Uint8Array,
      metaJson: string,
    ) => string,
  ) => void;
  clearImageHandler?: () => void;
  setFontHandler?: (handler: (family: string) => boolean) => void;
  clearFontHandler?: () => void;
  setTextMeasureHandler?: (handler: (requestJson: string) => string) => void;
  clearTextMeasureHandler?: () => void;
}

export interface ScriptLogDelivery {
  entries: ScriptLogEntry[];
  error?: string;
  meta?: ScriptRunMeta;
}

export interface ScriptRunMeta {
  frameId?: string;
  frameName?: string;
  hasUpdate: boolean;
  hasDraw: boolean;
  applied: boolean;
}

export interface RunFrameScriptOptions {
  engine: ScriptEngine;
  context?: ScriptDocumentContext;
  /** Called when Luau `apply_changes()` runs (may be mid-script, sync). */
  onApplyPages?: (runId: string, pagesJson: string) => void;
  /** Called asynchronously after the engine returns (never mid-print). */
  onLogs?: (delivery: ScriptLogDelivery) => void;
  meta?: Omit<ScriptRunMeta, "hasUpdate" | "hasDraw" | "applied">;
}

export interface RunFrameScriptOutcome extends ScriptRunResult {
  meta: ScriptRunMeta;
}

/** Schedule log/error delivery off the engine call stack. */
export function deliverScriptLogsAsync(
  delivery: ScriptLogDelivery,
  onLogs: (delivery: ScriptLogDelivery) => void,
): Promise<void> {
  return new Promise(resolve => {
    queueMicrotask(() => {
      onLogs(delivery);
      resolve();
    });
  });
}

/**
 * Run a frame script through `engine`, then asynchronously notify `onLogs`.
 * Resolves after log delivery has been scheduled and flushed.
 */
export async function runFrameScript(
  source: string,
  options: RunFrameScriptOptions,
): Promise<RunFrameScriptOutcome> {
  await preloadScriptFonts(source);
  if (options.onApplyPages && options.engine.setApplyHandler) {
    options.engine.setApplyHandler(options.onApplyPages);
  }
  try {
    const result = options.engine.runFrameScript(source, options.context);
    const meta: ScriptRunMeta = {
      ...options.meta,
      hasUpdate: result.hasUpdate,
      hasDraw: result.hasDraw,
      applied: result.applied,
    };
    if (options.onLogs) {
      await deliverScriptLogsAsync(
        {
          entries: result.logs,
          error: result.error,
          meta,
        },
        options.onLogs,
      );
    }
    return { ...result, meta };
  } finally {
    options.engine.clearApplyHandler?.();
  }
}

export function createWasmScriptEngine(wasm: CurvaScriptWasm): ScriptEngine {
  return {
    runFrameScript: (source, context) => wasm.runFrameScript(source, context),
    startSession: (source, context) => wasm.startSession(source, context),
    tickSession: dtSeconds => wasm.tickSession(dtSeconds),
    setInputState: json => wasm.setInputState(json),
    stopSession: () => wasm.stopSession(),
    setApplyHandler: handler => wasm.setApplyHandler(handler),
    clearApplyHandler: () => wasm.clearApplyHandler(),
    setImageHandler: handler => wasm.setImageHandler(handler),
    clearImageHandler: () => wasm.clearImageHandler(),
  };
}

let defaultEnginePromise: Promise<ScriptEngine | undefined> | undefined;
let defaultEngine: ScriptEngine | undefined;

/** Soft-load the Wasm engine used by the editor. */
export function ensureDefaultScriptEngine(): Promise<ScriptEngine | undefined> {
  if (!defaultEnginePromise) {
    defaultEnginePromise = (async () => {
      const wasm = await ensureCurvaScriptWasm();
      defaultEngine = wasm ? createWasmScriptEngine(wasm) : undefined;
      return defaultEngine;
    })();
  }
  return defaultEnginePromise;
}

/** Already-resolved default engine, if `ensureDefaultScriptEngine` finished. */
export function getDefaultScriptEngineIfReady(): ScriptEngine | undefined {
  return defaultEngine;
}

export function resetDefaultScriptEngineForTests(): void {
  defaultEnginePromise = undefined;
  defaultEngine = undefined;
}

function unavailableEngineResult(): ScriptRunResult {
  return {
    logs: [],
    error: "Script engine unavailable. Run `bun run build:curva-script`.",
    hasUpdate: false,
    hasDraw: false,
    applied: false,
    sessionAlive: false,
  };
}

/** Convenience for the editor: soft-load Wasm, run one-shot, async-deliver logs. */
export async function runAttachedFrameScript(
  source: string,
  options: Omit<RunFrameScriptOptions, "engine"> & { engine?: ScriptEngine } = {},
): Promise<RunFrameScriptOutcome | undefined> {
  const engine = options.engine ?? (await ensureDefaultScriptEngine());
  if (!engine) {
    const failure = unavailableEngineResult();
    if (options.onLogs) {
      await deliverScriptLogsAsync(
        {
          entries: failure.logs,
          error: failure.error,
          meta: {
            ...options.meta,
            hasUpdate: false,
            hasDraw: false,
            applied: false,
          },
        },
        options.onLogs,
      );
    }
    return {
      ...failure,
      meta: {
        ...options.meta,
        hasUpdate: false,
        hasDraw: false,
        applied: false,
      },
    };
  }
  return runFrameScript(source, { ...options, engine });
}

export interface StartAttachedFrameScriptOptions {
  engine?: ScriptEngine;
  context?: ScriptDocumentContext;
  onApplyPages?: (runId: string, pagesJson: string) => void;
  onLogs?: (delivery: ScriptLogDelivery) => void;
  meta?: Omit<ScriptRunMeta, "hasUpdate" | "hasDraw" | "applied">;
}

/**
 * Start a frame script session (load + `init`). Keeps the Wasm VM when
 * `update` / `draw` exist. Caller must {@link stopAttachedFrameScriptSession}
 * (or use {@link ScriptRealtimeController}) when finished.
 *
 * Leaves the apply handler registered while `sessionAlive` so ticks can call
 * `apply_changes`.
 */
export async function startAttachedFrameScript(
  source: string,
  options: StartAttachedFrameScriptOptions = {},
): Promise<RunFrameScriptOutcome | undefined> {
  const engine = options.engine ?? (await ensureDefaultScriptEngine());
  if (!engine?.startSession) {
    const failure = unavailableEngineResult();
    if (options.onLogs) {
      await deliverScriptLogsAsync(
        {
          entries: failure.logs,
          error: failure.error,
          meta: {
            ...options.meta,
            hasUpdate: false,
            hasDraw: false,
            applied: false,
          },
        },
        options.onLogs,
      );
    }
    return { ...failure, meta: { ...options.meta, hasUpdate: false, hasDraw: false, applied: false } };
  }

  if (options.onApplyPages && engine.setApplyHandler) {
    engine.setApplyHandler(options.onApplyPages);
  }

  await preloadScriptFonts(source);

  const result = engine.startSession(source, options.context);
  const meta: ScriptRunMeta = {
    ...options.meta,
    hasUpdate: result.hasUpdate,
    hasDraw: result.hasDraw,
    applied: result.applied,
  };

  if (options.onLogs) {
    await deliverScriptLogsAsync(
      {
        entries: result.logs,
        error: result.error,
        meta,
      },
      options.onLogs,
    );
  }

  if (!result.sessionAlive) {
    engine.clearApplyHandler?.();
    engine.stopSession?.();
  }

  return { ...result, meta };
}

export function stopAttachedFrameScriptSession(engine?: ScriptEngine): void {
  const target = engine ?? getDefaultScriptEngineIfReady();
  if (target) {
    target.stopSession?.();
    target.clearApplyHandler?.();
    return;
  }
  // Engine not loaded yet ⇒ no session exists. Do not schedule an async stop:
  // it can land after a subsequent startSession and kill realtime scripts.
}

export interface ScriptRealtimeControllerOptions {
  engine: ScriptEngine;
  /** Called before each tick to push keyboard/mouse JSON into the session. */
  getInputJson?: () => string;
  /** Called after each successful tick to clear wasPressed / wasReleased edges. */
  onAfterTick?: () => void;
  onPages?: (pagesJson: string) => void;
  onLogs?: (delivery: ScriptLogDelivery) => void;
  onError?: (error: string) => void;
  onExit?: () => void;
  onStop?: () => void;
  meta?: Omit<ScriptRunMeta, "hasUpdate" | "hasDraw" | "applied">;
  fps?: number;
}

/** Fixed-timestep host loop for an active Wasm script session. */
export class ScriptRealtimeController {
  private timer: ReturnType<typeof setInterval> | undefined;
  private stopped = false;
  private readonly dtSeconds: number;
  private readonly intervalMs: number;

  constructor(private readonly options: ScriptRealtimeControllerOptions) {
    const fps = options.fps ?? SCRIPT_REALTIME_FPS;
    this.dtSeconds = 1 / fps;
    this.intervalMs = 1000 / fps;
  }

  start(): void {
    if (this.timer !== undefined || this.stopped) {
      return;
    }
    this.timer = setInterval(() => this.tick(), this.intervalMs);
  }

  stop(): void {
    if (this.stopped) {
      return;
    }
    this.stopped = true;
    if (this.timer !== undefined) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
    this.options.engine.stopSession?.();
    this.options.engine.clearApplyHandler?.();
    this.options.onStop?.();
  }

  private tick(): void {
    if (this.stopped) {
      return;
    }
    const inputJson = this.options.getInputJson?.();
    if (inputJson !== undefined) {
      this.options.engine.setInputState?.(inputJson);
    }
    const tick = this.options.engine.tickSession?.(this.dtSeconds);
    if (!tick) {
      this.stop();
      return;
    }

    this.options.onAfterTick?.();

    if (tick.logs.length > 0 || tick.error) {
      const meta: ScriptRunMeta = {
        ...this.options.meta,
        hasUpdate: true,
        hasDraw: true,
        applied: tick.applied,
      };
      void deliverScriptLogsAsync(
        {
          entries: tick.logs,
          error: tick.error,
          meta,
        },
        delivery => {
          this.options.onLogs?.(delivery);
        },
      );
    }

    if (tick.pagesJson) {
      this.options.onPages?.(tick.pagesJson);
    }

    if (tick.error) {
      this.options.onError?.(tick.error);
      this.stop();
      return;
    }

    if (tick.exitRequested) {
      this.options.onExit?.();
      this.stop();
    }
  }
}
